import React from "react";
import { renderRichText } from 'gatsby-source-contentful/rich-text'
import { INLINES, BLOCKS, MARKS } from '@contentful/rich-text-types'
import styled from "styled-components";
import Image from "gatsby-image";
import { graphql, Link } from "gatsby";
import Layout from "../layouts/default";
import { Body,
  Header2,
  Header3,
  Header4,
  Header5,
  Header6 } from "../components/typography";
import { Colors } from "../components/styles/Colors";
import Breakpoints from "../components/styles/Breakpoints";
import { Helmet } from "react-helmet";

export const query = graphql`
  query BlogPost($id: String) {
    contentfulBlogPost(id: { eq: $id }) {
      title
      slug
      image {
        fixed(width: 680, height: 680) {
          ...GatsbyContentfulFixed_withWebp
        }
      }
      content {
        raw
				references {
					... on Node {
						... on ContentfulAsset {
							id
							title
							contentful_id
							description
							fluid {
								...GatsbyContentfulFluid
							}
						}
						... on ContentfulImageLink {
							id
							url
							contentful_id
							image {
								fluid {
									...GatsbyContentfulFluid
								}
							}
						}
						... on ContentfulImageExternalLink {
							id
							url
							contentful_id
							image {
								fluid {
									...GatsbyContentfulFluid
								}
							}
						}
					}
				}
      }
      category {
        color
        headline
      }
    }
  }
`;

const Wrapper = styled.div`
  position: relative;

  background: ${Colors.cream2};
`;

const Header = styled.div`
  margin: 0 auto 64px;
  padding: 64px 32px 0;

  text-align: center;

  color: ${Colors.cream1};

  position: relative;

  z-index: 1;

  ${Header2} {
    position: relative;
    margin-bottom: 32px;
  }

  .gatsby-image-wrapper {
    width: 310px;
    height: 310px;

    max-width: 100%;
  }

  &:before {
    content: "";

    position: absolute;
    top: 0;
    left: 0;

    width: 100%;
    height: 90%;

    z-index: 0;

    background: ${(props) => props.color};
  }

  ${Breakpoints.tablet} {
    .gatsby-image-wrapper {
      max-width: 640px;
      max-height: 640px;

      width: 100%;
      height: 100%;
    }
  }
`;

const Content = styled.div`
  padding: 0 32px 64px;

  color: ${Colors.grey};

  margin: 0 auto;
  max-width: 732px;

  p {
    margin: 15px 0;
  }
`;

export default function BlogPost({ data, location }) {
  const post = data.contentfulBlogPost;
	const options = {
		renderMark: {
			[MARKS.BOLD]: (text) => <b>{text}</b>,
			[MARKS.ITALIC]: (text) => <i>{text}</i>,
			[MARKS.UNDERLINE]: (text) => <u>{text}</u>,
			[MARKS.CODE]: (text) => <code>{text}</code>,
		},
		renderNode: {
			[INLINES.EMBEDDED_ENTRY]: (node, children) => {
				return (
					<div>{children}</div>
				)
			},
			[INLINES.ENTRY_HYPERLINK]: (node, children) => {
				const { uri } = node.data
				return (
					<a href={uri}>{children}</a>
				)
			},
			[INLINES.ASSET_HYPERLINK]: (node, children) => {
				const { uri } = node.data
				return (
					<a href={uri}>{children}</a>
				)
			},
			[INLINES.HYPERLINK]: (node, children) => {
				const { uri } = node.data
				return (
					<a href={uri}>{children}</a>
				)
			},
			[BLOCKS.OL_LIST]: (node, children) => {
				return <ol>{children}</ol>
			},
			[BLOCKS.UL_LIST]: (node, children) => {
				return <ul>{children}</ul>
			},
			[BLOCKS.LIST_ITEM]: (node, children) => {
				return <ul>{children}</ul>
			},
			[BLOCKS.PARAGRAPH]: (node, children) => {
				return <Body>{children}</Body>
			},
			[BLOCKS.HEADING_2]: (node, children) => {
				return <Header2>{children}</Header2>
			},
			[BLOCKS.HEADING_3]: (node, children) => {
				return <Header3>{children}</Header3>
			},
			[BLOCKS.HEADING_4]: (node, children) => {
				return <Header4>{children}</Header4>
			},
			[BLOCKS.HEADING_5]: (node, children) => {
				return <Header5>{children}</Header5>
			},
			[BLOCKS.HEADING_6]: (node, children) => {
				return <Header6>{children}</Header6>
			},
			[BLOCKS.QUOTE]: (node, children) => {
				return <blockquote>{children}</blockquote>
			},
			[BLOCKS.HR]: (node, children) => {
				return <hr/>
			},
			[BLOCKS.EMBEDDED_ASSET]: (node) => {
				const { fluid, description } = node.data.target
				return (
					<Image
						fluid={fluid}
						alt={description}
						style={{
							margin: '10px 0'
						}}
					/>
				)
			},
			[BLOCKS.EMBEDDED_ENTRY]: (node) => {
				const { image, url, __typename } = node.data.target
				if (__typename === 'ContentfulImageLink') {
					return <Link to={url}>
						<Image
							fluid={image.fluid}
							style={{
								margin: '10px 0'
							}}
						/>
					</Link>
				}
				if (__typename === 'ContentfulImageExternalLink') {
					return <a href={url} target='_blank' rel="noreferrer nofollow noopener">
						<Image
							fluid={image.fluid}
							style={{
								margin: '10px 0'
							}}
						/>
					</a>
				}
				return <></>
			},
		},
	}

  return (
    <Layout pathName={location.pathname}>
      <Helmet>
        <title>{post.title}</title>
        <meta property="og:image" content={post.image.fixed.src} />
      </Helmet>
      <Wrapper>
        <Header color={post.category.color}>
          <Header2>{post.title}</Header2>
          <Image fixed={post.image.fixed} />
        </Header>
        <Content>{post.content && renderRichText(post.content, options)}</Content>
      </Wrapper>
    </Layout>
  );
}
